import { Button } from "@/components/ui/button";
import { formatInr } from "../../utils/money";
import {
  ArrowUp,
  ChevronDown,
  ChevronUp,
  CirclePlus,
} from "lucide-react";
import EditServiceCharge from "pages/GenericOrderDetailsPage/components/edit_service_charge";
import EditRateQuantityModal from "pages/GenericOrderDetailsPage/components/EditRateQuantityModal";
import { Milestone, OrderDetailResponse } from "pages/GenericOrderDetailsPage/types/order";
import { useMemo, useState } from "react";
import DiscountEditDialog from "../AmountCharges/DiscountEditDialog";
import AddCurrencyDialog from "./AddCurremcy";
import AssignPrepaidCardDialog from "./AssignCard";
import TCSEditDialog from "pages/GenericOrderDetailsPage/components/tcs/TCSEditDialog";
import useOrderStageStatus from "pages/GenericOrderDetailsPage/hooks/useOrderStageStatus";
import { useForexAccess } from "pages/GenericOrderDetailsPage/hooks/useForexAccess";

export default function ProductAndCharges({
  orderdetails,
  disCountinfo,
  milestones,
  orderType,
  deliveryData,
  isInvoiceAvalaible
}: {
  orderdetails: OrderDetailResponse;
  disCountinfo: any;
  payable?: any;
  paid?: any;
  isDisabled?: boolean;
  milestones?: Milestone[];
  orderType?: string;
  deliveryData: any;
  isInvoiceAvalaible: boolean
}) {
  const { canEditOrder, canEditTcs, canEditServiceCharge } = useForexAccess();
  const OtherChargesVal = orderdetails?.overview?.amount_and_charges;
  const [openCouponModal, setOpenCouponModal] = useState<boolean>(false);
  const [showCharges, setShowCharges] = useState<boolean>(false);
  const [showGSTDetails, setShowGSTDetails] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isCurrencyDialogOpen, setIsCurrencyDialogOpen] = useState(false);
  const [prodcutTypeFlow, setProductTypeFlow] = useState<string>("Card");
  const [selectProductTypeList, setSelectProductTypeList] = useState<any>(null)

  const {
    completedStages,
    getAllButtonStates
  } = useOrderStageStatus(milestones || []);

  const handleOpen = () => {
    setOpenCouponModal(!openCouponModal);
  };

  const [assignLineId, setAssignLineId] = useState<string | null>(null);
  const handleAssignCard = (lineId: string) => {
    setAssignLineId(lineId);
    setOpen(true);
  };
  const getAmount = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const parsed =
      typeof value === "number"
        ? value
        : typeof value === "string"
          ? Number(value)
          : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatAmount = (value: unknown): string => {
    const amount = getAmount(value);
    return formatInr(amount ?? 0);
  };


  const gstCharges = OtherChargesVal?.margins_and_charges?.GST ?? {};
  const gstTotalFormatted = formatAmount(gstCharges?.GST);
  const cgstAmount = getAmount(gstCharges?.CGST);
  const sgstAmount = getAmount(gstCharges?.SGST);
  const igstAmount = getAmount(gstCharges?.IGST);
  const hasStateGST = cgstAmount !== null && sgstAmount !== null;

  const processedProducts = useMemo(() => {
    const products = orderdetails?.overview?.products || [];
    const onlyAcceptedProducts = products.filter(p => p.status !== 'Reject');

    return onlyAcceptedProducts.map((product) => {
      const fcyAmount = product.fx_amount || 0;
      const inrAmount = product.inr_amount || 0;
      const quantity = product.quantity || 1;
      const currencyWant = orderType === 'sell' ?  product.country_currency_have || "" : product.country_currency_want || "";
      const currencyHave = orderType === 'sell' ? product.country_currency_want || "" : product.country_currency_have || "";
      const rate = product.rate_and_margins?.[0]?.ibr_rate || 0;
      const customer = product.created_for || "";


      return {
        id: product.id,
        name: product.name || "",
        subCategory: product.subCategory || "",
        fcyAmount:
          fcyAmount > 0
            ? `${currencyWant} ${fcyAmount.toFixed(2)}`
            : `${currencyWant} ${((inrAmount / rate) * quantity).toFixed(2)}`,
        customerRate: `${currencyHave} ${rate > 0 ? rate.toFixed(2) : (inrAmount / quantity).toFixed(2)
          }`,
        inrAmount: inrAmount,
        quantity: quantity,
        status: product.status || "",
        currencyWant,
        currencyHave,
        rate: rate || inrAmount / quantity,
        customer,
        product_id: product.product_id || 0,
      };
    });
  }, [orderdetails?.overview?.products]);

  const groupedCardProducts = processedProducts.reduce((acc, product) => {
    const cardProducts = [16, 35, 36]
    const key = cardProducts.includes(product.product_id) ? 'card' : 'currency';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(product);
    return acc;
  }, {} as Record<string, typeof processedProducts>);

  const cardDetailsWithProducts = Object.entries(groupedCardProducts).map(
    ([type, products]) => {
      const onlyAcceptedProducts = products.filter((p) => p.status !== "Reject");
      const firstProductId = onlyAcceptedProducts?.[0]?.id ?? null;

      if (type === "card") {
        return {
          id: firstProductId,
          type: "card",
          products: onlyAcceptedProducts || [],
        };
      }

      return {
        id: firstProductId,
        products: onlyAcceptedProducts || [],
      };
    }
  );

  const customerId = orderdetails?.metadata?.customer_id;

  return (
    <div className="w-full ">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
          <div className="p-3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#333333] leading-[140%] tracking-[0.02rem]">
                Products
              </h2>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-3 text-sm font-medium text-[#666666] leading-[14px] border-b pb-3 bg-[#EEEEEE] p-[14px] rounded-tl-[8px] rounded-tr-[8px]">
              <div className=" flex items-center gap-2">
                Product
                <ArrowUp className="w-4 h-4" />
              </div>
              <div className="">FCY Amount</div>
              <div className="">Customer rate</div>
              <div className=""></div>
            </div>

            {processedProducts?.length > 0 ? (
              cardDetailsWithProducts?.map((product, key) => {
                if (product.type === 'card') {
                  return (
                    <div key={key} className="grid grid-cols-3 gap-4 items-start py-3 border-b border-gray-100">
                      <div className="flex flex-col place-content-start gap-2">
                        {(product.products ?? []).map((line: any) => {
                          // Each card line shows its own card, never another line's.
                          const lineCard = orderdetails?.line_cards?.[line.id];
                          if (lineCard) {
                            const locked =
                              getAllButtonStates?.disableAll?.disabled ||
                              !completedStages.includes("Order Accepted") ||
                              deliveryData?.some(
                                (d: any) =>
                                  d.product_id === line.id &&
                                  d.status !== "Pending"
                              );
                            return (
                              <div key={line.id} className="w-full rounded-[8px]" data-testid="line-card">
                                <div className="bg-[#F4F8FF] p-4 rounded-[10px] shadow-sm relative">
                                  {canEditOrder && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Change the card on ${line.currencyWant} ${line.quantity}`}
                                      disabled={locked}
                                      onClick={() => handleAssignCard(line.id)}
                                      className="absolute top-2 right-2 p-1 h-auto text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded disabled:cursor-not-allowed"
                                    >
                                      <img src="/images/editicon.svg" alt="" className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <div className="font-semibold text-[#262F63]">{lineCard.issuer || "Prepaid card"}</div>
                                  <div className="mt-2 text-sm text-[#262F63]">{`No: ••••${lineCard.last4}`}</div>
                                  <div className="mt-1 text-xs text-[#555555]">{`${line.currencyWant} ${line.quantity}`}</div>
                                </div>
                              </div>
                            );
                          }
                          return canEditOrder ? (
                            <Button
                              key={line.id}
                              onClick={() => handleAssignCard(line.id)}
                              variant="ghost"
                              disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Order Closed')}
                              className="bg-[#EFF4FF] p-2 rounded-[10px] text-blue-700 font-semibold leading-4 text-sm tracking-normal justify-start hover:bg-[#DCE8FF] disabled:cursor-not-allowed"
                            >
                              {`Assign card to ${line.currencyWant} ${line.quantity}`}
                            </Button>
                          ) : null;
                        })}
                      </div>
                      <div className="flex col-span-2 flex-col">
                        {product?.products?.map((product) => (
                          <div
                            key={key}
                            className="flex justify-between items-center gap-4 py-3 border-b border-gray-100"
                          >
                            <div className=" text-[#333333] text-sm font-medium leading-[16px] tracking-[0px]">
                              {product?.currencyWant} {product?.quantity}
                            </div>
                            <div className=" text-[#333333] text-sm font-medium leading-[16px] tracking-[0px]">
                              {product?.customerRate}
                            </div>
                            <div className="">
                              {canEditOrder && (
<EditRateQuantityModal
                                currency="INR"
                                rate={product?.rate}
                                quantity={product?.quantity}
                                productId={product.id}
                                productName={product.name}
                                oldPayable={OtherChargesVal?.total_payable}
                                disabled={getAllButtonStates?.disableAll?.disabled}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Edit ${product.currencyWant} ${product.quantity}`}
                                  disabled={getAllButtonStates?.disableAll?.disabled}
                                  className="flex cursor-pointer items-center gap-1.5 p-1.5 h-auto text-sm font-medium text-blue-700 hover:bg-[#E8F0FE] rounded disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <img src="/images/editicon.svg" alt="" className="w-4 h-4" />
                                  Edit
                                </Button>
                              </EditRateQuantityModal>
)}
                            </div>
                          </div>
                        ))}
                      </div>
                      {
                        !isInvoiceAvalaible && canEditOrder &&
                        <div className="flex w-full justify-end col-span-full">
                          <button
                            disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments')}
                            onClick={() => {
                              setProductTypeFlow("Card");
                              setIsCurrencyDialogOpen(true);
                              setSelectProductTypeList(product?.products)
                            }}
                            className="flex cursor-pointer rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 items-center gap-2 text-sm font-semibold px-3 py-2 mt-4 text-[#333333] transition-colors"
                          >
                            <CirclePlus className="w-4 h-4" />
                            Add currency
                          </button>
                        </div>
                      }
                    </div>
                  )
                }
                return (
                  <div
                    key={key}
                    className="grid grid-cols-3 gap-4 items-start py-3 border-b border-gray-100"
                  >
                    <div className=" place-content-start">
                      <div className="">
                        <div className="flex flex-col">
                          <div className="pl-3 text-[#333333] font-semibold leading-[16px] tracking-[0px]">
                            Currency Notes
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex col-span-2 flex-col">
                      {
                        product.products?.map((product, idx) => {
                          return (
                            <div key={idx} className="flex justify-between items-center gap-4 py-3 border-b border-gray-100">
                              <div className=" text-[#333333] text-sm font-medium leading-[16px] tracking-[0px]">
                                {product?.currencyWant} {product?.quantity}
                              </div>
                              <div className=" text-[#333333] text-sm font-medium leading-[16px] tracking-[0px]">
                                {product?.customerRate}
                              </div>
                              <div className="">
                                {canEditOrder && (
<EditRateQuantityModal
                                currency="INR"
                                rate={product?.rate}
                                quantity={product?.quantity}
                                productId={product.id}
                                productName={product.name}
                                oldPayable={OtherChargesVal?.total_payable}
                                disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments') || completedStages.includes('Order Cancelled')}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Edit ${product.currencyWant} ${product.quantity}`}
                                  disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments') || completedStages.includes('Order Cancelled')}
                                  className="flex cursor-pointer items-center gap-1.5 p-1.5 h-auto text-sm font-medium text-blue-700 hover:bg-[#E8F0FE] rounded disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <img src="/images/editicon.svg" alt="" className="w-4 h-4" />
                                  Edit
                                </Button>
                              </EditRateQuantityModal>
)}
                              </div>
                            </div>
                          )
                        })
                      }
                    </div>
                    {
                      !isInvoiceAvalaible && canEditOrder &&
                      <div className="flex w-full justify-end col-span-full">
                        <button
                          onClick={() => {
                            setProductTypeFlow("Currency");
                            setIsCurrencyDialogOpen(true);
                            setSelectProductTypeList(product?.products);
                          }}
                          disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments')}
                          className="flex cursor-pointer rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 items-center gap-2 text-sm font-semibold px-3 py-2 mt-4 text-[#333333] transition-colors"
                        >
                          <CirclePlus className="w-4 h-4" />
                          Add currency
                        </button>
                      </div>
                    }
                  </div>
                )
              })
            ) : (
              <div className="text-center py-8 text-[#555555]">
                No products found
              </div>
            )}
          </div>
        </div>

        {/* Calculation Section */}
        <div className="">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Calculation
          </h2>

          <div className="px-4 py-5 rounded-[12px] shadow-[0px_4px_4px_2px_#EFF1FB]">
            <div className="mb-6">
              <h3 className="text-[13px] capitalize font-medium text-[#165FE3] mb-3">
                {/* Ops say sale and encashment; buy and sell are the customer's words. */}
                {orderType === 'sell' ? 'encashment' : 'sale'}
              </h3>
              <div className="space-y-2">
                {processedProducts?.map((product: any) => (
                  <div
                    key={product.id}
                    className="flex justify-between items-center mt-1 mb-[14px] text-sm"
                  >
                    <span className="text-[#666666]">
                      {product.currencyWant}{" "}
                      <span className="text-[#333] mr-1">
                        {product.quantity}
                      </span>
                      <span className="text-[#5777B2]">@</span>{" "}
                      {product.currencyHave}{" "}
                      <span className="text-[#333] ml-1">
                        {product.rate.toFixed(2)}
                      </span>
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-[#222222] bg-[#F5F5F5] px-[6px] py-1 rounded-[8px]">
                        {product.currencyHave}
                      </span>
                      <span className="text-[#333333] leading-[16px] font-medium text-base">
                        {formatInr(product.inrAmount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t pt-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-[#333333]">
                  Order total
                </span>
                <span className="font-medium text-sm tracking-[-0.01rem] leading-[16px] text-[#333333]">
                  {formatInr(orderdetails?.amount_and_charges?.amount_before_tax)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <div
                className="flex items-center justify-between mb-2 bg-[#F4F4F4] p-3 rounded-[12px] cursor-pointer"
                onClick={() =>
                  setShowCharges((prev) => {
                    if (prev) {
                      setShowGSTDetails(false);
                    }
                    return !prev;
                  })
                }
              >
                <span className="text-sm text-[#000000]">
                  Additional charges
                </span>
                {showCharges ? (
                  <ChevronUp className="w-4 h-4 text-[#000000]" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[#000000]" />
                )}
              </div>
            </div>

            {showCharges && (
              <div className="space-y-3 mb-6">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setShowGSTDetails((prev) => !prev)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#555555]">GST</span>
                    {showGSTDetails ? (
                      <ChevronUp className="w-4 h-4 text-[#555555]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#555555]" />
                    )}
                  </div>
                  <span className="text-sm font-medium">
                    {gstTotalFormatted}
                  </span>
                </div>
                {showGSTDetails && (
                  <div className="pl-6 space-y-2 text-sm text-[#555555]">
                    {hasStateGST ? (
                      <>
                        <div className="flex justify-between items-center">
                          <span>CGST</span>
                          <span className="text-sm font-medium text-gray-800">
                            {formatAmount(gstCharges?.CGST)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>SGST</span>
                          <span className="text-sm font-medium text-gray-800">
                            {formatAmount(gstCharges?.SGST)}
                          </span>
                        </div>
                      </>
                    ) : (
                      igstAmount !== null && (
                        <div className="flex justify-between items-center">
                          <span>IGST</span>
                          <span className="text-sm font-medium text-gray-800">
                            {formatAmount(gstCharges?.IGST)}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                )}

                {orderType !== 'sell' && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#555555]">TCS</span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium">
                        {formatInr(OtherChargesVal?.margins_and_charges?.TCS)}
                      </span>
                      {canEditTcs && (
                        <TCSEditDialog
                          tcs={OtherChargesVal?.margins_and_charges?.TCS}
                          override={orderdetails?.tcsOverride}
                          disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Delivery') || completedStages.includes('Order Cancelled')}
                        />
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#555555]">
                    Delivery charges
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">
                      {orderType === 'sell' ? '-' : ''}
                      {formatInr(OtherChargesVal?.margins_and_charges?.service_charge?.amount)}
                    </span>
                    {canEditServiceCharge && (
                      <EditServiceCharge
                        amount={Number(OtherChargesVal?.margins_and_charges?.service_charge?.amount)}
                        override={orderdetails?.serviceChargeOverride}
                        disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments') || completedStages.includes('Order Cancelled')}
                      />
                    )}
                  </div>
                </div>                

                {/* A coupon applies to a sale, not to an encashment. */}
                {orderType !== 'sell' && (
                <div className="flex flex-col justify-between">
                  <div className="flex justify-between items-center w-full">
                    <div className="flex items-center gap-2 text-sm text-[#666666]">
                      <span>Discount</span>
                      {canEditOrder && (
                        <button
                          type="button"
                          onClick={handleOpen}
                          className="p-1 cursor-pointer hover:bg-gray-100 rounded transition-colors"
                          aria-label={
                            openCouponModal
                              ? "Hide discount options"
                              : "Show discount options"
                          }
                        >
                          {openCouponModal ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )}
                        </button>
                      )}
                    </div>

                    <div className="flex flex-row items-baseline gap-2">
                      {Number(disCountinfo?.amount_discounted) > 0 ? (
                        <span className="text-sm font-medium text-red-700">
                          -{formatInr(disCountinfo?.amount_discounted)}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-[#555555]">{formatInr(0)}</span>
                      )}
                    </div>
                  </div>

                  {canEditOrder && openCouponModal && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 ease-in-out">
                      <DiscountEditDialog
                        couponCode={
                          disCountinfo?.discount_coupon_id?.[0]?.coupon_name ||
                          ""
                        }
                        amountBeforeTax={Number(orderdetails?.amount_and_charges?.amount_before_tax)}
                        customerId={orderdetails?.metadata?.customer_id as unknown as string}
                        disabled={getAllButtonStates?.disableAll?.disabled || !completedStages.includes('Order Accepted') || completedStages.includes('Payments')}
                      />
                    </div>
                  )}
                </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#555555]">
                    Round off
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">
                      {formatInr(OtherChargesVal?.margins_and_charges?.GST?.round_off)}
                    </span>                    
                  </div>
                </div>
              </div>
            )}

            <div className="w-full min-h-[56px] flex justify-between items-center gap-4 bg-[#e0edff] text-blue-700 mt-5 font-semibold text-sm py-[17px] px-[17px] border-t border-blue-300 rounded-br-[12px] rounded-bl-[12px]">
              <span className="text-base font-semibold text-[#165FE3]">
                Grand total
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[#165FE3] text-base font-medium">
                  {formatInr(OtherChargesVal?.total_payable)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isCurrencyDialogOpen && (
        <AddCurrencyDialog
          setIsCurrencyDialogOpen={setIsCurrencyDialogOpen}
          prodcutType={"Buy"}
          isEncashment={orderType === 'sell'}
          selectedProductType={prodcutTypeFlow}
          ProdcutList={selectProductTypeList}
        />
      )}
      {open && (
        <AssignPrepaidCardDialog
          customerId={(customerId as unknown as string) ?? null}
          open={open}
          onOpenChange={(value) => { setOpen(value); if (!value) setAssignLineId(null); }}
          cardLines={processedProducts
            .filter((p: { product_id: number; id: string }) => [16, 35, 36].includes(p.product_id) && (assignLineId ? p.id === assignLineId : !orderdetails?.line_cards?.[p.id]))
            .map((p: { id: string; name: string; currencyWant: string; quantity: number }) => ({ id: p.id, label: `${p.name || "Card"} · ${p.currencyWant} ${p.quantity}` }))}
        />
      )}
    </div>
  );
}
